import React, { forwardRef, useId } from 'react';

export interface SelectOption {
  label: string;
  value: string | number;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options?: SelectOption[];
  fullWidth?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      fullWidth = false,
      className = '',
      disabled,
      id,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const controlId = id || generatedId;
    const feedbackId = `${controlId}-feedback`;
    const description = [describedBy, (error || helperText) ? feedbackId : undefined].filter(Boolean).join(' ') || undefined;
    const baseStyles = 'field-control appearance-none pr-10';
    const errorStyles = error ? 'border-status-danger focus:ring-status-danger/20 focus:border-status-danger' : 'border-border-default';

    return (
      <div className={`min-w-0 ${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label htmlFor={controlId} className="field-label">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={controlId}
            aria-invalid={error ? true : undefined}
            aria-describedby={description}
            className={`${baseStyles} ${errorStyles} ${className}`}
            disabled={disabled}
            {...props}
          >
            {options ? (
              options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            ) : (
              props.children
            )}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-text-muted">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {error && (
          <p id={feedbackId} className="mt-1.5 text-xs text-status-danger">{error}</p>
        )}
        {helperText && !error && (
          <p id={feedbackId} className="mt-1.5 text-xs text-text-muted">{helperText}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
